import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import { RootState } from "./store"
import { selectIsFleetManager } from "./permissionsSlice"
import {
  DocumentDriverContext,
  FinishedRouteAlert,
  Message,
  ReferenceRouteAlert,
  StopLateAlert,
} from "./messagesSlice"

export type DirectImage = {
  src: string
  message?: Message
}

/** Per-image metadata override used by ImageGallery when we already have
 * the document info client-side (e.g. fetched from a URL-lookup adhoc).
 * Kept here (rather than in ImageGallery.tsx) so the overlay slice can
 * type it without creating an import cycle. */
export type ImageMetadataOverride = {
  extractedMetadata?: Record<string, string>
  driverContext?: DocumentDriverContext
  documentType?: string
  blurry?: boolean
  remarks?: string
}

export type ImageGallery = {
  visible: boolean
  initialImage?: string
  messageId?: string
  directImage?: DirectImage
  /** Multiple direct images for navigation (same semantics as ImageGallery component prop) */
  directImages?: DirectImage[]
  /** Per-image metadata keyed by the MAPPED (same-origin) src URL */
  metadataOverrides?: Record<string, ImageMetadataOverride>
  drawerOpen?: boolean
  /**
   * When set, an external host (e.g. CmrSearchDialog) has taken ownership of
   * rendering the gallery. The default in-chat ImageGallery render in
   * ChatHistory must skip itself to avoid mounting two instances at once.
   */
  externalSource?: string
}

export type PDFViewer = {
  visible: boolean
  PDFMessage?: Message
  drawerOpen?: boolean
  /**
   * Same role as ImageGallery.externalSource — suppresses the default
   * in-chat PDFViewer render so the external host owns it.
   */
  externalSource?: string
}

export type TransportBriefViewer = {
  visible: boolean
  /** Presigned S3 URL of the markdown digest (or per-source MD file). */
  url?: string
  /** Optional title override (default: "Pełny briefing transportowy"). */
  title?: string
}

export type RouteAlert = {
  visible: boolean
  alertId?: string
  driverJid?: string
  updatedAt?: string
  type?: "reference" | "late" | "routeFinished"
  details?: ReferenceRouteAlert | StopLateAlert | FinishedRouteAlert
}

export type Search = {
  globalSearchVisible: boolean
  chatSearchVisible: boolean
}

export type Overlays = {
  imageGallery: ImageGallery
  PDFViewer: PDFViewer
  transportBriefViewer: TransportBriefViewer
  routeAlert: RouteAlert
  search: Search
  mobileMap: boolean
  archive: {
    visible: boolean
    lastActiveChatJid?: string
  }
  /** Which shape the left chat list takes. `null` until the user picks one
   * this session; the selector then falls back to the shape their role
   * starts with. Not persisted, so a reload returns to that default. */
  chatListMode: "chats" | "dispatchers" | null
}

type ShowArchiveAction = {
  lastActiveChatJid: string
}

const initialState: Overlays = {
  imageGallery: {
    visible: false,
  },
  PDFViewer: {
    visible: false,
  },
  transportBriefViewer: {
    visible: false,
  },
  routeAlert: {
    visible: false,
  },
  search: {
    globalSearchVisible: false,
    chatSearchVisible: false,
  },
  mobileMap: false,
  archive: {
    visible: false,
  },
  chatListMode: null,
}

export const overlaysSlice = createSlice({
  name: "overlays",
  initialState,
  reducers: {
    updateImageGallery: (
      state,
      action: PayloadAction<Partial<ImageGallery>>,
    ) => {
      return {
        ...state,
        imageGallery: {
          ...state.imageGallery,
          messageId: undefined,
          directImage: undefined,
          directImages: undefined,
          metadataOverrides: undefined,
          externalSource: undefined,
          ...action.payload,
        },
      }
    },
    /** Patch ONLY the metadataOverrides field on the current imageGallery
     * state without resetting directImages / directImage / messageId. */
    setImageGalleryMetadataOverrides: (
      state,
      action: PayloadAction<Record<string, ImageMetadataOverride> | undefined>,
    ) => {
      return {
        ...state,
        imageGallery: {
          ...state.imageGallery,
          metadataOverrides: action.payload,
        },
      }
    },
    showImageGallery: (state) => {
      return {
        ...state,
        imageGallery: {
          ...state.imageGallery,
          visible: true,
        },
      }
    },
    hideImageGallery: (state) => {
      return {
        ...state,
        imageGallery: {
          visible: false,
        },
      }
    },
    updateRouteAlert: (state, action: PayloadAction<Partial<RouteAlert>>) => {
      return {
        ...state,
        routeAlert: {
          ...state.routeAlert,
          ...action.payload,
        },
      }
    },
    hideRouteAlert: (state) => {
      return {
        ...state,
        routeAlert: {
          visible: false,
        },
      }
    },
    updatePDFViewer: (state, action: PayloadAction<Partial<PDFViewer>>) => {
      return {
        ...state,
        PDFViewer: {
          ...state.PDFViewer,
          externalSource: undefined,
          ...action.payload,
        },
      }
    },
    showPDFViewer: (state) => {
      return {
        ...state,
        PDFViewer: {
          ...state.PDFViewer,
          visible: true,
        },
      }
    },
    hidePDFViewer: (state) => {
      return {
        ...state,
        PDFViewer: {
          visible: false,
        },
      }
    },
    showTransportBriefViewer: (
      state,
      action: PayloadAction<{ url: string; title?: string }>,
    ) => {
      return {
        ...state,
        transportBriefViewer: {
          visible: true,
          url: action.payload.url,
          title: action.payload.title,
        },
      }
    },
    hideTransportBriefViewer: (state) => {
      return {
        ...state,
        transportBriefViewer: {
          visible: false,
        },
      }
    },
    updateSearch: (state, action: PayloadAction<Partial<Search>>) => {
      return {
        ...state,
        search: {
          ...state.search,
          ...action.payload,
        },
      }
    },
    showGlobalSearch: (state) => {
      return {
        ...state,
        search: {
          ...state.search,
          globalSearchVisible: true,
        },
      }
    },
    hideGlobalSearch: (state) => {
      return {
        ...state,
        search: {
          ...state.search,
          globalSearchVisible: false,
        },
      }
    },
    showChatSearch: (state) => {
      return {
        ...state,
        search: {
          ...state.search,
          chatSearchVisible: true,
        },
      }
    },
    hideChatSearch: (state) => {
      return {
        ...state,
        search: {
          ...state.search,
          chatSearchVisible: false,
        },
      }
    },
    showMobileMap: (state) => {
      return {
        ...state,
        mobileMap: true,
      }
    },
    hideMobileMap: (state) => {
      return {
        ...state,
        mobileMap: false,
      }
    },
    showArchive: (state, action: PayloadAction<ShowArchiveAction>) => {
      return {
        ...state,
        archive: {
          visible: true,
          lastActiveChatJid: action.payload.lastActiveChatJid,
        },
      }
    },
    hideArchive: (state) => {
      return {
        ...state,
        archive: {
          visible: false,
        },
      }
    },
    setChatListMode: (
      state,
      action: PayloadAction<"chats" | "dispatchers">,
    ) => {
      state.chatListMode = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const selectOverlays = (state: RootState) => {
  return state.overlays
}

export const selectImageGallery = createSelector(
  [selectOverlays],
  (overlays) => overlays.imageGallery,
)

export const selectRouteAlert = createSelector(
  [selectOverlays],
  (overlays) => overlays.routeAlert,
)

export const isImageGalleryVisible = createSelector(
  [selectImageGallery],
  (imageGallery) => imageGallery.visible,
)

export const isRouteAlertVisible = createSelector(
  [selectRouteAlert],
  (routeAlert) => routeAlert.visible,
)

export const selectPDFViewer = createSelector(
  [selectOverlays],
  (overlays) => overlays.PDFViewer,
)

export const isPDFViewerVisible = createSelector(
  [selectPDFViewer],
  (PDFViewer) => PDFViewer.visible,
)

export const selectTransportBriefViewer = createSelector(
  [selectOverlays],
  (overlays) => overlays.transportBriefViewer,
)

export const isTransportBriefViewerVisible = createSelector(
  [selectTransportBriefViewer],
  (viewer) => viewer.visible,
)

export const selectSearch = createSelector(
  [selectOverlays],
  (overlays) => overlays.search,
)

export const isGlobalSearchVisible = createSelector(
  [selectSearch],
  (search) => search.globalSearchVisible,
)

export const isChatSearchVisible = createSelector(
  [selectSearch],
  (search) => search.chatSearchVisible,
)

export const isMobileMapVisible = createSelector(
  [selectOverlays],
  (overlays) => overlays.mobileMap,
)

export const isArchiveVisible = createSelector(
  [selectOverlays],
  (overlays) => overlays.archive.visible,
)

export const selectLastActiveChatJid = createSelector(
  [selectOverlays],
  (overlays) => overlays.archive.lastActiveChatJid,
)

/** A fleet manager opens on the fleet grouped by dispatcher, everyone else
 * on the plain list, until they toggle. */
export const selectChatListMode = (state: RootState): "chats" | "dispatchers" =>
  state.overlays.chatListMode ??
  (selectIsFleetManager(state) ? "dispatchers" : "chats")

export const {
  updateImageGallery,
  setImageGalleryMetadataOverrides,
  showImageGallery,
  hideImageGallery,
  updatePDFViewer,
  showPDFViewer,
  hidePDFViewer,
  showTransportBriefViewer,
  hideTransportBriefViewer,
  updateSearch,
  showGlobalSearch,
  hideGlobalSearch,
  showChatSearch,
  hideChatSearch,
  showMobileMap,
  hideMobileMap,
  showArchive,
  hideArchive,
  updateRouteAlert,
  hideRouteAlert,
  setChatListMode,
} = overlaysSlice.actions
export default overlaysSlice.reducer
